import React, { useMemo, useContext } from 'react'
import { GridColDef } from '@mui/x-data-grid-pro'
import { TableHeaderContext } from 'components/Shared/MuiTable/PartiTable'
import { OptionsState } from 'particles/Dropdown'
import { isSortable } from 'components/utils'
import MenuItem from 'particles/MenuItem'
import { TableViewType } from '..'
import { useActiveFilter } from '../hooks'

interface FilterListState {
    label: string
    value: string
}

interface GridColDefProps extends GridColDef {
    subFields?: {
        field: string
        headerName: string
    }[]
}

interface GetFilterOrSortTooltipMenuItemProps {
    title: string
    isSort: boolean
    onChange: (option: OptionsState) => void
    onClose?: () => void
    viewType?: TableViewType
}

export const getFilterOrSortTooltipMenuItem = ({
    title,
    isSort,
    onChange,
    onClose,
    viewType,
}: GetFilterOrSortTooltipMenuItemProps): React.ReactElement[] => {
    const { columns } = useContext(TableHeaderContext)
    const filters = useActiveFilter(viewType)
    const items: React.ReactElement[] = useMemo(() => {
        const isFilterable = (field: string) => {
            return Boolean(Object.keys(filters).find(filterKey => filterKey.includes(field)))
        }

        const filterOrSortList: FilterListState[] = columns.flatMap((item: GridColDefProps) => {
            if (item.field === 'username') return []

            if (item.subFields?.length) {
                return item.subFields
                    .filter(sub => (isSort ? isSortable(sub.field) : isFilterable(sub.field)))
                    .map(sub => ({
                        label: sub.headerName,
                        value: sub.field,
                    }))
            }

            const isValid = isSort ? isSortable(item.field) : isFilterable(item.field)
            if (!isValid) return []

            return [
                {
                    label: item.headerName || '',
                    value: item.field,
                },
            ]
        })
        const menuItemItems = filterOrSortList.map((item, index) => (
            <MenuItem.Text
                key={index}
                tabIndex={index}
                onClick={() => {
                    onChange(item)
                    onClose?.()
                }}
                text={item.label}
                value={item.value}
            />
        ))
        return menuItemItems.length > 0
            ? [<MenuItem.Title key="title" text={title.toUpperCase()} />, ...menuItemItems]
            : []
    }, [columns.length, isSort, filters, onChange])
    return items
}
